import {
    getOrgConfig,
    updateBrandLetterhead,
    type DocumentNumbering,
    type DocumentNumberingPrefix,
    type NumberedDocumentType,
} from '@api/organization';
import { getOrganizationIdFromStorage } from '@components/containers/procurementOrders/formComponents';
import useToaster from '@hooks/toaster/useToaster';
import {
    ActionIcon,
    Button,
    Group,
    Radio,
    SegmentedControl,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FC } from 'react';

import classes from './DocumentNumberingTab.module.css';

/** The document types that carry a brand-defined series, and what to call them on screen. */
const DOCUMENT_TYPES: Array<{ value: NumberedDocumentType; label: string; sample: string }> = [
    { value: 'PRODUCTION_ORDER', label: 'Production orders', sample: 'PO-2026-' },
    { value: 'PURCHASE_ORDER', label: 'Purchase orders', sample: 'PO-' },
    { value: 'SALES_ORDER', label: 'B2B sales orders', sample: 'SO-' },
];

/**
 * How this brand numbers its own orders.
 *
 * THE PREFIX IS THE WHOLE FEATURE. Every brand already has a house format — Mokobara's orders
 * read BANGE-0007 and FLIEGER-0002, Troovy's carry their own stem — and until now the only way
 * to get one was to type the entire number by hand on every order, which is how a series ends up
 * with two 0007s and a gap at 0009.
 *
 * EACH PREFIX COUNTS ON ITS OWN. That is the point of a list rather than one box: BANGE- and
 * FLIEGER- are two series, and a shared counter would leave each of them full of the numbers the
 * other took.
 *
 * NOTHING HERE HOLDS A COUNT. The next number is read off the orders that exist, so this screen
 * can never disagree with them — there is no counter to drift, reset, or reconcile after an
 * import.
 */
const DocumentNumberingTab: FC = () => {
    const { showToastSuccess, showToastError } = useToaster();
    const queryClient = useQueryClient();
    const organizationId = useMemo(() => getOrganizationIdFromStorage(), []);

    const [documentType, setDocumentType] = useState<NumberedDocumentType>('PRODUCTION_ORDER');
    const [numbering, setNumbering] = useState<DocumentNumbering>({});
    const [dirty, setDirty] = useState(false);

    const orgQuery = useQuery({
        queryKey: ['org-config', organizationId],
        queryFn: () => getOrgConfig(organizationId as string),
        enabled: !!organizationId,
    });

    /*
     * Seeded once from the server and then owned locally, like the letterhead tab beside it. A
     * refetch mid-edit that overwrote half-typed prefixes would lose work with no warning.
     */
    useEffect(() => {
        if (!orgQuery.data || dirty) return;
        setNumbering(orgQuery.data.documentNumbering ?? {});
    }, [orgQuery.data, dirty]);

    const prefixes: DocumentNumberingPrefix[] = numbering[documentType]?.prefixes ?? [];

    const editPrefixes = (next: DocumentNumberingPrefix[]) => {
        setDirty(true);
        setNumbering((current) => ({
            ...current,
            [documentType]: { ...(current[documentType] ?? {}), prefixes: next },
        }));
    };

    const updateValue = (index: number, value: string) =>
        editPrefixes(prefixes.map((entry, i) => (i === index ? { ...entry, value } : entry)));

    /*
     * One default per type, so choosing a new one clears the old. A list with two defaults has no
     * answer to "which does a new order start with", and the server would just take the first.
     */
    const makeDefault = (index: number) =>
        editPrefixes(prefixes.map((entry, i) => ({ ...entry, isDefault: i === index })));

    const removePrefix = (index: number) => {
        const remaining = prefixes.filter((_, i) => i !== index);
        // Removing the default leaves the list with none, and the first would silently inherit
        // the job. Say so by promoting it explicitly.
        if (!remaining.some((entry) => entry.isDefault) && remaining.length > 0) {
            remaining[0] = { ...remaining[0], isDefault: true };
        }
        editPrefixes(remaining);
    };

    const addPrefix = () =>
        editPrefixes([...prefixes, { value: '', isDefault: prefixes.length === 0 }]);

    const save = useMutation({
        mutationFn: () =>
            updateBrandLetterhead({
                // Only the type being edited is sent. The server leaves the others alone, so
                // saving production-order prefixes cannot drop the purchase-order ones.
                documentNumbering: {
                    [documentType]: { prefixes: prefixes.filter((entry) => entry.value.trim()) },
                },
            }),
        onSuccess: () => {
            setDirty(false);
            void queryClient.invalidateQueries({ queryKey: ['org-config', organizationId] });
            showToastSuccess({ title: 'Numbering saved' });
        },
        onError: (error: any) =>
            showToastError({
                title: 'Could not save the numbering',
                subtitle: error?.error?.message ?? error?.message ?? 'Something went wrong.',
            }),
    });

    const active = DOCUMENT_TYPES.find((type) => type.value === documentType);

    return (
        <Stack gap={16} className={classes.tab}>
            <div>
                <Text fz={14} fw={600}>
                    Order numbering
                </Text>
                <Text fz={12} c="dimmed" mt={2}>
                    Define the stems your order numbers start with. Each one counts separately,
                    and the number after it is filled in for you.
                </Text>
            </div>

            <SegmentedControl
                value={documentType}
                onChange={(value) => setDocumentType(value as NumberedDocumentType)}
                data={DOCUMENT_TYPES.map((type) => ({ value: type.value, label: type.label }))}
            />

            <Stack gap={8}>
                {prefixes.length === 0 && (
                    <Text fz={12} c="dimmed">
                        No prefix set — new orders are numbered{' '}
                        <strong>{active?.sample}0001</strong>.
                    </Text>
                )}

                {prefixes.map((entry, index) => (
                    // Index-keyed deliberately: a prefix has no id, and its value is what the
                    // person is mid-way through typing — keying on that remounts on every stroke.
                    // eslint-disable-next-line react/no-array-index-key
                    <Group key={index} gap={12} align="center" wrap="nowrap">
                        <TextInput
                            placeholder="BANGE-"
                            value={entry.value}
                            onChange={(event) => updateValue(index, event.currentTarget.value)}
                            className={classes.prefixInput}
                        />
                        <Text fz={12} c="dimmed" className={classes.preview}>
                            {entry.value.trim() ? `${entry.value.trim()}0001` : ' '}
                        </Text>
                        <Radio
                            checked={entry.isDefault === true}
                            onChange={() => makeDefault(index)}
                            label="Default"
                        />
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label="Remove prefix"
                            onClick={() => removePrefix(index)}
                        >
                            <TrashIcon size={14} />
                        </ActionIcon>
                    </Group>
                ))}

                <Group gap={8}>
                    <Button
                        size="xs"
                        variant={ButtonVariant.SUBTLE}
                        leftSection={<PlusIcon size={14} />}
                        onClick={addPrefix}
                    >
                        Add prefix
                    </Button>
                </Group>
            </Stack>

            <Group>
                <Button
                    onClick={() => save.mutate()}
                    loading={save.isLoading}
                    disabled={!dirty || !organizationId}
                >
                    Save
                </Button>
            </Group>
        </Stack>
    );
};

export default DocumentNumberingTab;
